/**
 * Per-user Order Queue badge visibility, saved to the account.
 *
 * Toggles are local and instant: they replace AuthContext's `uiPreferences`
 * slot (never `profile`, so the organization is not refetched). The account
 * save goes out once, when the Display menu closes (or the page unmounts with
 * the menu still open, for example browser back), and only if something
 * changed while it was open. The save sends ONLY the `orderQueue` section; the
 * server merges it into the stored settings, so a stale tab cannot wipe a
 * section it never touched. A failed save shows a toast that stays until
 * dismissed and restores the `orderQueue` section from when the menu opened,
 * unless something newer happened since (a later toggle, reset or save), in
 * which case the newer local state stays and the next close re-sends it.
 *
 * Reading costs nothing: the settings ride on users.getProfile.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { UiPreferences } from '@threadheads/shared';
import { useAuth } from '@/contexts/AuthContext';
import { trpc } from '@/lib/trpc';
import {
  resolveBadgeVisibility,
  type OrderQueueBadgeKey,
  type OrderQueueBadgeVisibility,
} from '../display-prefs';

export interface OrderQueueDisplay {
  visibility: OrderQueueBadgeVisibility;
  /** Local and instant; no network. */
  setBadge: (key: OrderQueueBadgeKey, on: boolean) => void;
  /** Back to the defaults (an empty badge record); local and instant. */
  reset: () => void;
  /** Wire to the Display menu: open takes a snapshot, close sends at most ONE save. */
  onMenuOpenChange: (open: boolean) => void;
}

function withBadges(
  prefs: UiPreferences,
  badges: Record<string, boolean>,
): UiPreferences {
  // Spreading keeps sections and orderQueue keys this build does not know.
  return { ...prefs, orderQueue: { ...prefs.orderQueue, badges } };
}

/** `prefs` with its orderQueue section replaced by `section` (removed when undefined). */
function withOrderQueue(prefs: UiPreferences, section: UiPreferences['orderQueue']): UiPreferences {
  const { orderQueue: _drop, ...rest } = prefs;
  return section === undefined ? rest : { ...rest, orderQueue: section };
}

const sameSection = (a: UiPreferences, b: UiPreferences) =>
  JSON.stringify(a.orderQueue ?? null) === JSON.stringify(b.orderQueue ?? null);

export function useOrderQueueDisplay(): OrderQueueDisplay {
  const { t } = useTranslation('production');
  const { uiPreferences, setUiPreferences } = useAuth();
  const { mutateAsync } = trpc.users.updateUiPreferences.useMutation();

  const mutateRef = useRef(mutateAsync);
  mutateRef.current = mutateAsync;

  // The current settings. Written synchronously by setBadge / reset, because a
  // Reset selection and the menu close fire in the same event, before React
  // re-renders; the close must read this ref, not a render closure.
  const latestRef = useRef<UiPreferences>(uiPreferences);
  useEffect(() => {
    latestRef.current = uiPreferences;
  }, [uiPreferences]);

  const snapshotRef = useRef<UiPreferences>(uiPreferences);
  // Bumped by every toggle, reset and save start: a failed save restores only
  // when nothing newer happened after it started.
  const seqRef = useRef(0);
  // Set when a stale save failed while newer local state existed: the next
  // close must re-send even if nothing changed during that menu session.
  const retryOnCloseRef = useRef(false);

  const apply = useCallback(
    (next: UiPreferences) => {
      seqRef.current += 1;
      latestRef.current = next;
      setUiPreferences(next);
    },
    [setUiPreferences],
  );

  const setBadge = useCallback(
    (key: OrderQueueBadgeKey, on: boolean) => {
      const latest = latestRef.current;
      apply(withBadges(latest, { ...latest.orderQueue?.badges, [key]: on }));
    },
    [apply],
  );

  const reset = useCallback(() => {
    apply(withBadges(latestRef.current, {}));
  }, [apply]);

  const save = useCallback(async () => {
    const latest = latestRef.current;
    const snapshot = snapshotRef.current;
    if (!retryOnCloseRef.current && sameSection(latest, snapshot)) return;

    seqRef.current += 1;
    const mySeq = seqRef.current;
    try {
      // mutateAsync, not per-call mutate callbacks: TanStack Query v5 runs
      // per-call callbacks only for the most recent call, so an older save's
      // failure would be silently dropped. Only this hook's section is sent;
      // the server merges it into the stored settings.
      await mutateRef.current({ orderQueue: latest.orderQueue ?? {} });
      if (seqRef.current === mySeq) retryOnCloseRef.current = false;
    } catch {
      // Stays until dismissed (DESIGN.md: errors never auto-dismiss).
      toast.error(t('orders.display.saveFailed'), { duration: Infinity });
      if (seqRef.current === mySeq) {
        const restored = withOrderQueue(latestRef.current, snapshot.orderQueue);
        latestRef.current = restored;
        setUiPreferences(restored);
      } else {
        retryOnCloseRef.current = true;
      }
    }
  }, [setUiPreferences, t]);

  // Browser back / forward unmounts the page without ever firing
  // onOpenChange(false), so an open menu's changes are saved on unmount too.
  const menuOpenRef = useRef(false);
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(
    () => () => {
      if (menuOpenRef.current) {
        menuOpenRef.current = false;
        void saveRef.current();
      }
    },
    [],
  );

  const onMenuOpenChange = useCallback(
    (open: boolean) => {
      menuOpenRef.current = open;
      if (open) {
        snapshotRef.current = latestRef.current;
        return;
      }
      void save();
    },
    [save],
  );

  const visibility = useMemo(() => resolveBadgeVisibility(uiPreferences), [uiPreferences]);

  return { visibility, setBadge, reset, onMenuOpenChange };
}
